import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, ShoppingCart, Truck, Leaf, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [subscriptionType, setSubscriptionType] = useState('one-time');
  const { addToCart } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId, navigate]);

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please sign in to add items to cart');
      navigate('/login');
      return;
    }
    try {
      await addToCart(product.product_id, quantity, subscriptionType);
      toast.success(`${product.name} added to cart!`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const getPrice = () => {
    if (!product) return 0;
    if (subscriptionType === 'one-time') return product.price;
    return product.price * (1 - product.subscription_discount);
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="pt-20 min-h-screen bg-cream" data-testid="product-detail-page">
      <div className="container-custom py-12">
        {/* Back Link */}
        <Link to="/shop" className="inline-flex items-center gap-2 text-forest font-manrope font-medium mb-8 hover:gap-3 transition-all" data-testid="back-to-shop">
          <ArrowLeft className="w-5 h-5" />
          Back to Shop
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Image */}
          <div className="relative rounded-[2rem] overflow-hidden aspect-square">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
              data-testid="product-image"
            />
            {product.is_organic && (
              <span className="absolute top-6 left-6 badge-organic">
                Organic
              </span>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-col">
            <span className="text-sm font-manrope font-medium text-forest uppercase tracking-wider mb-2">
              {product.category}
            </span>
            
            <h1 className="font-playfair text-4xl md:text-5xl font-bold text-dark mb-4" data-testid="product-name">
              {product.name}
            </h1>

            <div className="flex items-center gap-4 mb-6">
              {product.farm_name ? (
                <span className="text-forest font-manrope" data-testid="product-farm">
                  Sourced from {product.farm_name}{product.farm_location ? `, ${product.farm_location}` : ''}
                </span>
              ) : (
                <span className="text-dark/40 font-manrope italic" data-testid="product-farm">
                  Source Coming Soon
                </span>
              )}
              <span className="text-dark/40">|</span>
              <span className="text-dark/60 font-manrope">{product.serving_size}</span>
            </div>

            <p className="font-manrope text-dark/70 text-lg leading-relaxed mb-8" data-testid="product-description">
              {product.description}
            </p>

            {/* Subscription Options */}
            <div className="mb-8">
              <h3 className="font-manrope font-bold text-dark mb-4">Delivery Schedule</h3>
              <RadioGroup value={subscriptionType} onValueChange={setSubscriptionType} className="grid grid-cols-2 gap-3" data-testid="subscription-options">
                {[
                  { value: 'one-time', label: 'One-Time', desc: 'Single delivery' },
                  { value: 'weekly', label: 'Weekly', desc: 'Every week' },
                  { value: 'biweekly', label: 'Bi-Weekly', desc: 'Every 2 weeks' },
                  { value: 'monthly', label: 'Monthly', desc: 'Every month' }
                ].map((option) => (
                  <Label
                    key={option.value}
                    htmlFor={option.value}
                    className={`flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      subscriptionType === option.value
                        ? 'border-forest bg-forest/5'
                        : 'border-forest/20 hover:border-forest/40'
                    }`}
                  >
                    <RadioGroupItem value={option.value} id={option.value} className="sr-only" />
                    <span className="font-manrope font-bold text-dark">{option.label}</span>
                    <span className="text-xs text-dark/60">{option.desc}</span>
                  </Label>
                ))}
              </RadioGroup>
              {subscriptionType !== 'one-time' && (
                <p className="text-xs text-forest font-manrope mt-3" data-testid="subscription-discount-note">
                  New subscribers get 10% off their first subscribed order &mdash; applied automatically at checkout.
                </p>
              )}
            </div>

            {/* Quantity */}
            <div className="flex items-center gap-6 mb-8">
              <span className="font-manrope font-bold text-dark">Quantity</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-full border-2 border-forest/20 flex items-center justify-center hover:border-forest transition-colors"
                  data-testid="quantity-decrease"
                >
                  <Minus className="w-4 h-4 text-dark" />
                </button>
                <span className="font-manrope font-bold text-xl w-12 text-center" data-testid="quantity-value">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 rounded-full border-2 border-forest/20 flex items-center justify-center hover:border-forest transition-colors"
                  data-testid="quantity-increase"
                >
                  <Plus className="w-4 h-4 text-dark" />
                </button>
              </div>
            </div>

            {/* Price & Add to Cart */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 p-6 bg-white rounded-2xl border border-forest/10">
              <div>
                <p className="font-playfair text-4xl font-bold text-dark" data-testid="product-price">
                  ${(getPrice() * quantity).toFixed(2)}
                </p>
                {subscriptionType !== 'one-time' && (
                  <p className="text-sm text-forest font-manrope">Save ${((product.price - getPrice()) * quantity).toFixed(2)}</p>
                )}
              </div>
              <button
                onClick={handleAddToCart}
                className="btn-primary flex-1 w-full sm:w-auto justify-center"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart
              </button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 pt-8 border-t border-forest/10">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-forest" />
                <span className="text-sm font-manrope text-dark/70">Free delivery over $50</span>
              </div>
              <div className="flex items-center gap-3">
                <Leaf className="w-5 h-5 text-forest" />
                <span className="text-sm font-manrope text-dark/70">Locally sourced</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-forest" />
                <span className="text-sm font-manrope text-dark/70">Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
